import React, { useEffect, useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { WarningCircleIcon } from '@phosphor-icons/react'
import { SocketChat } from '../SocketChat'
import { ModalProvider, ModalOutlet } from '../../../modals'
import { EVENTS, trackEvent } from '../../../../events'
import { useWorkspaceMetadata } from '../../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import { ScoopSpinner, ScoopText } from '../../../ui'
import styles from './EmbeddedSocketChat.module.scss'

// Error reasons to user-friendly messages
const ERROR_MESSAGES = {
    token_expired:
        'Your session has expired. Please refresh the page or contact your administrator.',
    token_revoked: 'Your access has been revoked. Please contact your administrator.',
    invalid_token: 'Authentication failed. The provided token is invalid.',
    invalid_signature: 'Authentication failed. The provided token is invalid.',
    invalid_token_type: 'Authentication failed. Invalid token type.',
    token_not_found: 'Invalid session. Please try again.',
    insufficient_permissions: 'You do not have permission to access this workspace.',
    default: 'Authentication failed. Please try again or contact support.',
}

/**
 * Decode JWT payload without validation (validation happens server-side)
 * @param {string} token - JWT token
 * @returns {object|null} - Decoded payload or null if invalid format
 */
const decodeJwtPayload = (token) => {
    try {
        const parts = token.split('.')
        if (parts.length !== 3) return null
        const payload = parts[1]
        const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
        return JSON.parse(decoded)
    } catch {
        return null
    }
}

export const EmbeddedSocketChat = () => {
    const dispatch = useDispatch()
    const [searchParams] = useSearchParams()
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [authError, setAuthError] = useState(null)
    const [isInitializing, setIsInitializing] = useState(true)
    const [selectedInbox, setSelectedInbox] = useState(null)
    const { workspaceMetadata } = useWorkspaceMetadata()

    // Get token from URL
    const urlToken = searchParams.get('token')

    // Get user-friendly error message
    const getErrorMessage = (code) => {
        return ERROR_MESSAGES[code] || ERROR_MESSAGES.default
    }

    // Handle WebSocket auth error (called from SocketChat or useWebSocket)
    const handleWebSocketAuthError = useCallback((error) => {
        const code = error?.code || 'default'
        setAuthError(getErrorMessage(code))
        setIsAuthenticated(false)

        // Notify parent window about token expiry
        if (code === 'token_expired' && window.parent !== window) {
            window.parent.postMessage({ type: 'SCOOP_EMBED_TOKEN_EXPIRED' }, '*')
        }
    }, [])

    // Initialize with URL token
    const initializeWithUrlToken = useCallback(() => {
        if (!urlToken) {
            setAuthError('No authentication token provided.')
            setIsInitializing(false)
            return
        }

        // Decode JWT to extract workspaceId and userContext (no validation here)
        const payload = decodeJwtPayload(urlToken)

        if (!payload) {
            setAuthError('Invalid token format.')
            setIsInitializing(false)
            return
        }

        const { workspaceId, userContext, sub, organizationId } = payload

        if (!workspaceId) {
            setAuthError('Token missing required workspace information.')
            setIsInitializing(false)
            return
        }

        // Set workspace in Redux
        dispatch({
            type: 'SWITCH_WORKSPACE',
            workspaceID: workspaceId,
            workspaceName: 'Embedded Workspace',
        })

        // Set authentication in Redux - server will validate the actual token
        dispatch({
            type: 'SET_AUTHENTICATED',
            isAuthenticated: true,
            isEmbeddedUser: true,
            user: {
                signInUserSession: {
                    idToken: { jwtToken: urlToken },
                    refreshToken: { token: urlToken },
                },
                username: userContext?.id || sub,
                attributes: {
                    email: userContext?.email || null,
                    userFirstName: userContext?.name || null,
                },
            },
        })

        setIsAuthenticated(true)
        setIsInitializing(false)

        // Log event
        trackEvent(EVENTS.AUTH.EMBEDDED_CHAT, {
            userID: userContext?.id || sub,
            workspaceID: workspaceId,
            organizationID: organizationId,
        })
    }, [dispatch, urlToken])

    // Main initialization effect
    useEffect(() => {
        initializeWithUrlToken()
    }, [initializeWithUrlToken])

    // Set the first inbox as default if none is selected
    useEffect(() => {
        if (!selectedInbox && workspaceMetadata?.inboxes?.length > 0) {
            const firstInboxWithData = workspaceMetadata.inboxes.find((inbox) =>
                inbox.tables?.some((table) => table.columns?.length > 0)
            )
            if (firstInboxWithData) setSelectedInbox(firstInboxWithData)
        }
    }, [workspaceMetadata, selectedInbox])

    // Error state
    if (authError) {
        return (
            <div className={styles.errorContainer}>
                <div className={styles.errorAlert}>
                    <div className={styles.errorHeader}>
                        <WarningCircleIcon size={20} weight="fill" className={styles.errorIcon} />
                        <ScoopText className={styles.errorTitle}>Authentication Error</ScoopText>
                    </div>
                    <ScoopText className={styles.errorMessage}>{authError}</ScoopText>
                </div>
            </div>
        )
    }

    // Loading state
    if (isInitializing || !isAuthenticated) {
        return (
            <div className={styles.loadingContainer}>
                <ScoopSpinner />
                <ScoopText className={styles.loadingTitle}>Loading...</ScoopText>
            </div>
        )
    }

    return (
        <ModalProvider>
            <div className={styles.mainContainer}>
                <SocketChat
                    isEmbedded={true}
                    onAuthError={handleWebSocketAuthError}
                    selectedInbox={selectedInbox}
                />
            </div>
            <ModalOutlet />
        </ModalProvider>
    )
}
